import React from "react";

import { Control, FieldError, useForm } from "react-hook-form";

import {
  // Text
  QuestionText,
  QuestionTextPreview,
  isQuestionText,
  // Selector Single
  QuestionSelectorSingle,
  QuestionSelectorSinglePreview,
  isQuestionSelectorSingle,
  // Selector Multiple
  QuestionSelectorMultiple,
  QuestionSelectorMultiplePreview,
  isQuestionSelectorMultiple,
  // Skill
  QuestionSkill,
  QuestionSkillPreview,
  isQuestionSkill,
  // Evaluation
  QuestionEvaluation,
  QuestionEvaluationPreview,
  isQuestionEvaluation,
} from "./QuestionsImpl";
import { compact } from "lodash";
import AnswerHistoryModal from "./AnswerHistoryModal";
import { useToggle } from "react-use";
import { AnswerFragment, AnswerInput, QuestionFragment, ReportReviewFragment } from '../../../graphql/generated/definitions';
import useTranslations from '../../../hooks/useTranslations';

type ReportFormInputs = {
  answers: AnswerInput[];
};

export interface QuestionImplProps {
  question: QuestionFragment;
  answers: AnswerFragment[];
  role: string;
  review: ReportReviewFragment;
  control: Control<ReportFormInputs>;
  register: ReturnType<typeof useForm>["register"];
  setValue: ReturnType<typeof useForm>["setValue"];
  errors: FieldError;
  status: QuestionFormStatus;
  name: string;
  displayIndex: string;
  toggleHistoryModalOpen;
  needsToBeFilled: boolean;
  onReview: boolean;
  autoFocus: boolean;
}

export interface QuestionPreviewImplProps {
  question: QuestionFragment;
  displayIndex?: string;
}

export type QuestionConfig<T extends QuestionFragment> = {
  Component: React.ElementType<QuestionImplProps>;
  PreviewComponent: React.ElementType<QuestionPreviewImplProps>;
  typeValidator: (q: QuestionFragment) => q is T;
};

const questionConfigs: QuestionConfig<QuestionFragment>[] = [
  {
    Component: QuestionSelectorSingle,
    PreviewComponent: QuestionSelectorSinglePreview,
    typeValidator: isQuestionSelectorSingle,
  },
  {
    Component: QuestionSelectorMultiple,
    PreviewComponent: QuestionSelectorMultiplePreview,
    typeValidator: isQuestionSelectorMultiple,
  },
  {
    Component: QuestionText,
    PreviewComponent: QuestionTextPreview,
    typeValidator: isQuestionText,
  },
  {
    Component: QuestionSkill,
    PreviewComponent: QuestionSkillPreview,
    typeValidator: isQuestionSkill,
  },
  {
    Component: QuestionEvaluation,
    PreviewComponent: QuestionEvaluationPreview,
    typeValidator: isQuestionEvaluation,
  },
];

export type AnswersByRole<T extends AnswerFragment> = {
  [key in Exclude<"collaborator" | "referents" | "mine", "administrator">]: T[];
};

export function getAnswersByRole<T extends AnswerFragment>(
  answers: AnswerFragment[],
  parseStrategy: (r: AnswerFragment[]) => T[],
  review: ReportReviewFragment,
  viewerId: string
): AnswersByRole<T> {
  const answersByRole = {
    collaborator: parseStrategy(
      answers.filter((answer) => answer.user.id === review.collaborator.id)
    ),
    referents: parseStrategy(
      // TODO: Uncomment for correct parsing of referents
      answers.filter((answer) =>
        review.referents.map((r) => r.id).includes(answer.user.id)
      )
    ),
    mine: parseStrategy(
      compact([answers.find((answer) => answer.user.id === viewerId)])
    ),
  };

  return answersByRole;
}

export type QuestionFormStatus =
  | "to_be_filled_collaborator"
  | "to_be_filled_evaluator"
  | "collaborator_review"
  | "evaluator_review"
  | "hr_review";

type QuestionProps = {
  question: QuestionFragment;
  answers: AnswerFragment[];
  review: ReportReviewFragment;
  control: Control<ReportFormInputs>;
  register: ReturnType<typeof useForm>["register"];
  setValue: ReturnType<typeof useForm>["setValue"];
  errors: FieldError;
  displayIndex: string;
  name: string;
  status: QuestionFormStatus;
  role: string;
  autoFocus: boolean;
};

type QuestionPreviewProps = {
  question: QuestionFragment;
  index: number;
  displayIndex?: string;
  preview: true;
  status: QuestionFormStatus;
};

function Question(props: QuestionProps);
function Question(props: QuestionPreviewProps);
function Question({
  question,
  status,
  ...props
}: QuestionProps | QuestionPreviewProps) {
  const t = useTranslations();
  const Config = questionConfigs.find((c) => c.typeValidator(question));
  const [historyModalOpen, toggleHistoryModalOpen] = useToggle(false);

  if ("preview" in props) {
    return Config ? (
      <div id={`questionPreview-${question.id}`}>
        <Config.PreviewComponent question={question} {...props} />
      </div>
    ) : (
      <div>{t("appErrors.componentUnknown", { key: question.type })}</div>
    );
  }

  const needsToBeFilled =
    status === "to_be_filled_evaluator" || status === "to_be_filled_collaborator";
  const onReview =
    status === "collaborator_review" ||
    status === "evaluator_review" ||
    status === "hr_review";

  return Config ? (
    <div id={`question-${question.id}`}>
      <Config.Component
        question={question}
        needsToBeFilled={needsToBeFilled}
        onReview={onReview}
        status={status}
        toggleHistoryModalOpen={toggleHistoryModalOpen}
        {...props}
      />
      {historyModalOpen && (
        <AnswerHistoryModal
          review={props.review}
          open={historyModalOpen}
          questionDigest={question.digest}
          onClose={toggleHistoryModalOpen}
        />
      )}
    </div>
  ) : (
    <div>{t("appErrors.componentUnknown", { key: question.type })}</div>
  );
}

export default React.memo(Question);
